// src/api/api.js
import axios from 'axios';

// Option 1: Use proxy (RECOMMENDED for development – no hard-coded port)
const api = axios.create({
  // No baseURL here → Create React App proxy will forward all requests to http://localhost:8006
});

// Only fills in Authorization from 'access_token' when the caller hasn't
// already set one explicitly — AskOS (askos_token), Samkalp Admin and
// Studio/Classroom all share this axios instance but use different
// localStorage keys, so an unconditional overwrite here silently replaced
// e.g. a student's askos_token with a stale admin access_token whenever
// both were present in the same browser, causing spurious 401s/logouts.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const API_URL = process.env.REACT_APP_API_URL || '';

// Helper to get current tenant slug from JWT payload
const getTenantSlug = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.tenant_slug || null;
  } catch (e) {
    console.error("Failed to decode JWT for tenant slug");
    return null;
  }
};

// API endpoints — all tenant-specific routes use /tenant/{slug}/...
//export const login = (slug, email, password) =>
  //api.post(`/tenant/${slug}/token`, new URLSearchParams({ username: email, password }));

//API endpoint for system admin login / ...

/* export const login = (slug, email, password, isSystemAdmin = false) => {
  const path = isSystemAdmin ? '/token' : `/tenant/${slug}/token`;
  return api.post(path, new URLSearchParams({ username: email, password }));
}; */

export const login = (slug = null, email, password) => {
  const path = slug ? `/tenant/${slug}/token` : '/token';
  return api.post(path, new URLSearchParams({
    username: email,
    password: password
  }));
};

// --- NEW: Public Registration Endpoints ---

/**
 * Step 1: Submit institutional metadata to trigger verification email.
 * @param {Object} data - TenantSelfRegistration payload
 */
export const registerTenant = (data) => {
  return api.post('/public/register-tenant', data);
};

/**
 * Step 2: Verify the JWT token from the email link.
 * @param {string} token - The registration_verification token
 */
export const verifyEmail = (token) => {
  return api.get(`/public/verify-email?token=${token}`);
};


export const getProjects = () => {
  const slug = getTenantSlug();
  if (!slug) throw new Error('Tenant slug not available');
  return api.get(`/tenant/${slug}/projects`);
};

export const uploadCurriculum = (projectId, file) => {
  const slug = getTenantSlug();
  if (!slug) throw new Error('Tenant slug not available');
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/tenant/${slug}/upload-curriculum/${projectId}`, formData);
};

export const getProjectAssets = (projectId) => {
  const slug = getTenantSlug();
  if (!slug) throw new Error('Tenant slug not available');
  return api.get(`/tenant/${slug}/projects/${projectId}/assets`);
};

export const approveReview = (reviewId, comments = "") => {
  const slug = getTenantSlug();
  if (!slug) throw new Error('Tenant slug not available');
  return api.post(`/tenant/${slug}/approve-review/${reviewId}`, { comments });
};

export const rejectReview = (reviewId, comments) => {
  const slug = getTenantSlug();
  if (!slug) throw new Error('Tenant slug not available');
  return api.post(`/tenant/${slug}/reviews/${reviewId}/reject`, { comments });
};

export const saveCurriculumMapping = (projectId, formData) => {
  const slug = getTenantSlug();
  if (!slug) throw new Error("Tenant slug missing");
  return api.post(`/tenant/${slug}/save-curriculum-mapping/${projectId}`, formData);
};

// System Admin APIs
export const getAllTenants = () =>
  api.get('/admin/tenants');

export const createTenant = (data) =>
  api.post('/admin/tenants', data);

export const updateTenantStatus = (tenantId, status) =>
  api.put(`/admin/tenants/${tenantId}/status`, { status });

export const getTenantHierarchy = (tenantId) =>
  api.get(`/admin/tenants/${tenantId}/hierarchy`);


export const getAllProjects = () =>
  api.get('/admin/projects');

export const getAllUsers = () =>
  api.get('/admin/users');

export const getGlobalTaxonomies = () => 
  api.get('/admin/taxonomy-standards');

export const createGlobalTaxonomy = (payload) => 
  api.post('/admin/taxonomy-standards', payload);

// Update your existing createTenantProject to ensure taxonomy_id is passed
//export const createTenantProject = (slug, payload) =>
//  api.post(`/tenant/${slug}/projects`, payload); 
  // Note: payload will now include { name, program_id, taxonomy_id }


// System Admin – Tenant Admins
export const getTenantAdmins = (tenantId) =>
  api.get(`/admin/tenants/${tenantId}/admins`);

export const createTenantAdmin = (tenantId, data) =>
  api.post(`/admin/tenants/${tenantId}/admins`, data);

export const resetUserPassword = (userId, newPassword) =>
  api.put(`/admin/users/${userId}/reset-password`, { new_password: newPassword });

export const getSystemAdminMetrics = () =>
  api.get('/admin/dashboard/metrics');

export const getAuditLogs = (limit = 20) =>
  api.get(`/admin/audit-logs?limit=${limit}`);

// Tenant Admin
export const getTenantDashboardMetrics = (slug) =>
  api.get(`/tenant/${slug}/dashboard/metrics`);

export const getTenantPrograms = (slug) =>
  api.get(`/tenant/${slug}/programs`);

export const getTenantProjects = (slug) =>
  api.get(`/tenant/${slug}/projects`);

export const getTenantAuditLogs = (slug, limit = 20) =>
  api.get(`/tenant/${slug}/audit-logs?limit=${limit}`);

export const createTenantProgram = (slug, payload) =>
  api.post(`/tenant/${slug}/programs`, payload);

export const updateTenantProgramStatus = (slug, programId, status) =>
  api.patch(`/tenant/${slug}/programs/${programId}/status`, null, {
    params: { status }
  });

export const getTenantAdminProjects = (slug) =>
  api.get(`/tenant/${slug}/admin/projects`);

export const createTenantProject = (slug, payload) =>
  api.post(`/tenant/${slug}/projects`, payload);

export const getTenantUsers = (slug) =>
  api.get(`/tenant/${slug}/users`);

export const createTenantUser = (slug, payload) =>
  api.post(`/tenant/${slug}/users`, payload);

export const updateTenantUser = (slug, userId, payload) =>
  api.patch(`/tenant/${slug}/users/${userId}`, payload);


export const resetTenantUserPassword = (slug, userId, payload) =>
  api.post(`/tenant/${slug}/users/${userId}/reset-password`, payload);

export const deleteTenantUser = (slug, userId) =>
  api.delete(`/tenant/${slug}/users/${userId}`);


export const assignUserToProject = (slug, projectId, payload) =>
  api.post(`/tenant/${slug}/projects/${projectId}/assign`, payload);

export const getProjectAssignments = (slug, projectId) =>
  api.get(`/tenant/${slug}/projects/${projectId}/assignments`);

/**
 * Fetches the full JSON config for a specific taxonomy
 */
export const getTaxonomyDetails = (slug, taxonomyId) => {
  return api.get(`/tenant/${slug}/taxonomy-standards/${taxonomyId}`);
};

/**
 * Clones a global taxonomy to create a tenant-specific version
 */
export const cloneTaxonomyStandard = (slug, taxonomyId, data) => {
  return api.post(`/tenant/${slug}/taxonomy-standards/${taxonomyId}/clone`, data);
};

export const getTenantTaxonomies = (slug) => 
  api.get(`/tenant/${slug}/taxonomy-standards`);

export const initializeCurriculum = (tenantSlug, projectId, payload) => {
  return api.post(`/tenant/${tenantSlug}/projects/${projectId}/initialize-curriculum`, payload);
};

export const getContentStandards = (slug) => {
  return api.get(`/tenant/${slug}/content-standards`);
};

/** Full JSON detail of one content standard (sections, weights, budgets, etc.) */
export const getContentStandardDetail = (slug, standardId) =>
  api.get(`/tenant/${slug}/content-standards/${standardId}`);

/** Create a tenant-specific content standard (clone + customise). tenant_admin only. */
export const createContentStandard = (slug, payload) =>
  api.post(`/tenant/${slug}/content-standards`, payload);

export const updateProjectContentStandard = (slug, projectId, standardId) => {
  return api.post(`/tenant/${slug}/project/${projectId}/content-standard`, {
    content_standard_id: standardId
  });
};

export const getUnitContent = (slug, projectId, unitTitle) =>
  api.get(`/tenant/${slug}/projects/${projectId}/content/${encodeURIComponent(unitTitle)}`);

/**
 * Fetches unit content by ID instead of Title.
 * Matches the new main.py endpoint.
 */
export const getUnitContentById = (slug, projectId, unitId) => {
  return api.get(`/tenant/${slug}/projects/${projectId}/unit-content/${unitId}`);
};

/**
 * Handles GCS asset uploads for placeholders (Images/Graphs/Diagrams).
 * This supports your "Additional 3" requirement.
 */
export const uploadProjectAsset = (slug, projectId, formData) => {
  return api.post(`/tenant/${slug}/projects/${projectId}/upload-asset`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const updateUnitContent = (slug, blockId, payload) =>
  api.put(`/tenant/${slug}/content/${blockId}`, payload);

export const getGraphSnapshot = (slug, projectId) =>
  api.get(`/tenant/${slug}/projects/${projectId}/graph-snapshot`);

export const getUnitDownloadUrl = (slug, projectId, unitId) => {
  const baseUrl = window.location.origin || process.env.REACT_APP_API_URL || "https://content.brahmagpt.ai/api";
  return `${baseUrl}/tenant/${slug}/projects/${projectId}/unit-download/${unitId}?format=docx`;
};

// Recharge a tenant's wallet
export const rechargeTenantWallet = (payload) => 
  api.post('/admin/wallets/recharge', payload);

export const getTenantWalletHistory = (tenantId) => 
  api.get(`/admin/wallets/${tenantId}/history`);

// --- Assessment & Academic Profile Endpoints ---

/** 1. Fetch Full Academic Profile for the Authority View */
export const getFullProjectProfile = (projectId) => 
  api.get(`/tenant/projects/${projectId}/full-profile`);

/** 2. Save or Update the Assessment Blueprint */
export const saveAssessmentBlueprint = (slug, projectId, payload) => 
  api.post(`/tenant/${slug}/projects/${projectId}/blueprints`, payload);

/** 3. Fetch Unique Generation Runs (Batches) */
export const getAssessmentHistory = (slug, projectId) => 
  api.get(`/tenant/${slug}/projects/${projectId}/assessment-history`);

/** 4. Fetch Questions for a specific Batch (using timestamp) */
export const getAssessmentBank = (slug, projectId, timestamp = null) => {
  const url = `/tenant/${slug}/projects/${projectId}/assessment-bank`;
  return api.get(timestamp ? `${url}?timestamp=${timestamp}` : url);
};

/** 5. SME Question Status Update (Approve/Flag) */
export const updateQuestionStatus = (slug, projectId, questionId, status) => 
  api.patch(`/tenant/${slug}/projects/${projectId}/questions/${questionId}/status`, { status });

/** 6. Faculty Asset Upload (Multipart Form Data) */
export const uploadAssessmentAsset = (slug, projectId, questionId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(
    `/tenant/${slug}/projects/${projectId}/questions/${questionId}/upload-asset`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

/** 7. Download Approved Bank (DOCX/PDF) */
export const exportAssessmentBank = (slug, projectId, format) => 
  api.get(`/tenant/${slug}/projects/${projectId}/export-assessment?format=${format}`, {
    responseType: 'blob' // Required for binary file handling
  });

/**
 * Triggers the project-wide AI generation of the question bank.
 * Linked to POST /tenant/{slug}/projects/{project_id}/generate-assessment-bank
 */
export const triggerFullProjectAssessment = async (slug, projectId) => {
  return await api.post(`/tenant/${slug}/projects/${projectId}/generate-assessment-bank`);
};

/**
 * Fetches the count of all active AI tasks across the entire tenant.
 * Used by the AppHeader for the Admin Global Task Observer.
 */
export const getActiveTasksCount = async (slug) => {
    return await api.get(`/tenant/${slug}/admin/active-tasks-count`);
};


// ── Book analysis preflight API helpers ───────────────────────────────────────

/**
 * Trigger preflight analysis for a single book (TOC + page count).
 * Returns immediately; poll getBookAnalysisStatus() for results.
 */
export const analyseBook = (tenantSlug, projectId, payload) =>
  api.post(`/tenant/${tenantSlug}/projects/${projectId}/analyse-book`, payload);

/**
 * Poll for book analysis status.
 * Returns { status, page_count, toc, estimated_blocks, estimated_credits }
 */
export const getBookAnalysisStatus = (tenantSlug, projectId, bookHash) =>
  api.get(`/tenant/${tenantSlug}/projects/${projectId}/book-analysis-status`, {
    params: { book_hash: bookHash },
  });

/**
 * Confirm chapter selection after TOC review.
 * Triggers block extraction + GCS upload in background.
 */
export const confirmChapters = (tenantSlug, projectId, payload) =>
  api.post(`/tenant/${tenantSlug}/projects/${projectId}/confirm-chapters`, payload);

/**
 * Check whether all books for a project are ready (analysis_status == 'ready').
 * Used to gate the Initiate Curriculum button.
 */
export const getBooksReadyStatus = (tenantSlug, projectId) =>
  api.get(`/tenant/${tenantSlug}/projects/${projectId}/books-ready`);

/**
 * Initializes a context-locked CoPilot session for a specific unit.
 */
export const initCopilotSession = (slug, projectId, unitId) => {
    return api.post(`/tenant/${slug}/projects/${projectId}/units/${unitId}/copilot/init`);
};

/**
 * Sends a message to the CoPilot for refinement.
 */
export const sendCopilotMessage = (slug, projectId, sessionId, message) => {
    return api.post(`/tenant/${slug}/projects/${projectId}/copilot/chat`, {
        session_id: sessionId,
        message: message
    });
};

/**
 * Triggers the pypandoc export for a specific session.
 */
export const exportCopilotAsset = (slug, projectId, sessionId, assetType) => {
    return api.post(`/tenant/${slug}/projects/${projectId}/copilot/export`, {
        session_id: sessionId,
        asset_type: assetType
    });
};

// ─── Academic Evidence Dashboards ────────────────────────────────────────────

export const getEvidenceMeta = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/meta`);

export const getEvidenceCurriculumCoverage = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/curriculum-coverage`);

export const getEvidenceSourceGrounding = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/source-grounding`);

export const getEvidenceSLMStructure = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/slm-structure`);

export const getEvidenceAssessmentCoverage = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/assessment-coverage`);

export const getEvidenceBloomDistribution = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/bloom-distribution`);

export const getEvidenceGaps = (slug, projectId, refresh = false) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/gaps`, { params: { refresh } });

export const saveEvidenceSnapshot = (slug, projectId, dashboardType, data) =>
    api.post(`/tenant/${slug}/projects/${projectId}/evidence/snapshot`, {
        dashboard_type: dashboardType,
        data,
    });

export const getEvidenceSnapshots = (slug, projectId, dashboardType = null) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/snapshots`, {
        params: dashboardType ? { dashboard_type: dashboardType } : {},
    });

export const fillMissingSLMSections = (slug, projectId, blockId, stepNames) =>
    api.post(`/tenant/${slug}/projects/${projectId}/evidence/slm-structure/${blockId}/fill-sections`, {
        step_names: stepNames,
    });

export const downloadEvidenceDocx = (slug, projectId, dashboardType) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/${dashboardType}/export-docx`, {
        responseType: 'blob',
    });

// ─── V2 Evidence Dashboards ───────────────────────────────────────────────────

export const getEvidenceOutcomeMapping = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/outcome-mapping`);

export const getEvidenceConceptOutcomeTraceability = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/concept-outcome-traceability`);

export const getEvidenceRubricEvidence = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/rubric-evidence`);

export const getEvidenceGroundedAssessment = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/grounded-assessment`);

export const getEvidenceAskOSEngagement = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/askos-engagement`);

export const getEvidenceConceptDifficulty = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/concept-difficulty`);

/** Trigger the DeepSeek outcome mapping engine for a project. */
export const runOutcomeMapping = (slug, projectId) =>
    api.post(`/tenant/${slug}/projects/${projectId}/evidence/run-outcome-mapping`);

/** Outcome mappings indexed by concept_id — used by AssessmentBankView LO/PO/PSO sidebar. */
export const getConceptOutcomes = (slug, projectId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/concept-outcomes`);

// Re-export with blueprint_id filter support
export const getEvidenceAssessmentCoverageFiltered = (slug, projectId, blueprintId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/assessment-coverage`,
        { params: blueprintId ? { blueprint_id: blueprintId } : {} });

export const getEvidenceBloomDistributionFiltered = (slug, projectId, blueprintId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/bloom-distribution`,
        { params: blueprintId ? { blueprint_id: blueprintId } : {} });

export const getEvidenceRubricEvidenceFiltered = (slug, projectId, blueprintId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/rubric-evidence`,
        { params: blueprintId ? { blueprint_id: blueprintId } : {} });

export const getEvidenceGroundedAssessmentFiltered = (slug, projectId, blueprintId) =>
    api.get(`/tenant/${slug}/projects/${projectId}/evidence/grounded-assessment`,
        { params: blueprintId ? { blueprint_id: blueprintId } : {} });

/**
 * Fetches projects assigned to the logged-in faculty member.
 */
export const getMyFacultyProjects = (slug) => {
    return api.get(`/tenant/${slug}/my-faculty-projects`);
};

// --- Classroom Authentication & Branding ---
/**
 * Fetches branding (Logo/Name) based on slug before login.
 */
export const getTenantBranding = (slug) => {
    return api.get(`/public/tenant-branding/${slug}`);
};

/**
 * Separate Login for Classroom Users (Faculty).
 */
export const classroomLogin = (slug, credentials) => {
    return api.post(`/tenant/${slug}/classroom/token`, new URLSearchParams(credentials));
};

// --- Classroom User Management (Studio Admin Side) ---
/**
 * Provisions a Studio User into the Classroom environment.
 */
export const promoteToClassroom = (slug, userData) => {
    return api.post(`/tenant/${slug}/classroom/users`, userData);
};

// ── Assessment Bank: Push to Moodle & Classroom ──────────────────────────────

export const pushAssessmentToMoodle = (slug, projectId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/push-to-moodle`, data).then((r) => r.data);

export const pushAssessmentToClassroom = (slug, projectId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/push-to-classroom`, data).then((r) => r.data);

// Reuses ExamOS Moodle config (same tenant, same auth token)
export const listMoodleCoursesForAssessment = (slug) =>
  api.get(`/tenant/${slug}/exam/settings/moodle/courses`).then((r) => r.data);

export const listClassroomCoursesForAssessment = (slug, projectId, accessToken) =>
  api.get(`/tenant/${slug}/projects/${projectId}/classroom-courses`, { params: { access_token: accessToken } }).then((r) => r.data);

// ── Teaching Copilot: New Generation APIs ────────────────────────────────────

export const generateLessonPlan = (slug, projectId, conceptId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/concepts/${conceptId}/lesson-plan`, data).then((r) => r.data);

export const generateCourseLessonPlan = (slug, projectId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/lesson-plan`, data).then((r) => r.data);

export const pushMaterialToMoodle = (slug, projectId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/copilot/push-material-moodle`, data).then((r) => r.data);

export const pushMaterialToClassroom = (slug, projectId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/copilot/push-material-classroom`, data).then((r) => r.data);

export const getCopilotProfile = (slug) =>
  api.get(`/tenant/${slug}/copilot/me`).then((r) => r.data);

export const updateCopilotProfile = (slug, data) =>
  api.patch(`/tenant/${slug}/copilot/me`, data).then((r) => r.data);

export const generateLabSession = (slug, projectId, conceptId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/concepts/${conceptId}/lab-session`, data).then((r) => r.data);

export const generateQuiz = (slug, projectId, conceptId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/concepts/${conceptId}/quiz/generate`, data).then((r) => r.data);

export const publishQuiz = (data) =>
  api.post(`/quiz/publish`, data).then((r) => r.data);

export const pushQuizLinkToClassroom = (slug, projectId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/copilot/push-quiz-link`, data).then((r) => r.data);

export const listCopilotClassroomCourses = (accessToken) =>
  api.get(`/copilot/classroom-courses`, { params: { access_token: accessToken } }).then((r) => r.data);

export const getQuizMonitor = (quizId) =>
  api.get(`/quiz/${quizId}/monitor`).then((r) => r.data);

export const startQuizTimer = (quizId) =>
  api.post(`/quiz/${quizId}/start-timer`).then((r) => r.data);

export const uploadCopilotMaterial = (slug, projectId, formData) =>
  api.post(`/tenant/${slug}/projects/${projectId}/copilot/upload-material`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

export const generateContentFromText = (slug, projectId, conceptId, data) =>
  api.post(`/tenant/${slug}/projects/${projectId}/concepts/${conceptId}/generate-content`, data).then((r) => r.data);

// ─── Module Subscriptions ─────────────────────────────────────────────────────

export const getTenantModules = (slug) =>
  api.get(`/tenant/${slug}/modules`).then((r) => r.data);

export const setTenantModules = (tenantId, modules) =>
  api.patch(`/admin/tenants/${tenantId}/modules`, { modules }).then((r) => r.data);

// ─── Subscription Analytics ───────────────────────────────────────────────────

export const getSystemSubscriptionAnalytics = () =>
  api.get('/admin/analytics/subscriptions').then((r) => r.data);

export const getStaffCreditAnalytics = (slug) =>
  api.get(`/tenant/${slug}/analytics/staff-credits`).then((r) => r.data);

export const getStudentCreditAnalytics = (slug) =>
  api.get(`/tenant/${slug}/analytics/student-credits`).then((r) => r.data);

// ─── Tenant Moodle Integration (Admin, uses regular access_token) ─────────────

export const getTenantMoodleConfig = (slug) =>
  api.get(`/tenant/${slug}/exam/settings/moodle`).then((r) => r.data);

export const saveTenantMoodleConfig = (slug, data) =>
  api.put(`/tenant/${slug}/exam/settings/moodle`, data).then((r) => r.data);

export const testTenantMoodleConnection = (slug) =>
  api.post(`/tenant/${slug}/exam/settings/moodle/test-connection`).then((r) => r.data);

export const listTenantMoodleCourses = (slug) =>
  api.get(`/tenant/${slug}/exam/settings/moodle/courses`).then((r) => r.data);

export default api;